import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { UpdateSelector } from "@/components/tree-changes/UpdateSelector";
import { ChangesTable } from "@/components/tree-changes/ChangesTable";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TreeChanges = () => {
  const [selectedUpdate, setSelectedUpdate] = useState<string>("all");
  const [compareUpdate, setCompareUpdate] = useState<string>("all");
  const [selectedChangeType, setSelectedChangeType] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const { data: updateOptions } = useQuery({
    queryKey: ["tree-changes-updates"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('categories_atp_autoteile_de')
          .select('created_at')
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching updates:', error);
          toast.error('Error fetching updates');
          throw error;
        }
        
        if (!data) {
          return [];
        }
        
        const processedData = data.map(item => {
          if (!item.created_at) return null;
          const date = new Date(item.created_at);
          const hour = date.getHours().toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          
          return `${hour}h ${day}-${month}-${year}`;
        }).filter(Boolean) as string[];
        
        return [...new Set(processedData)];
      } catch (error) {
        console.error('Error processing updates:', error);
        toast.error('Error processing updates');
        throw error;
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    if (updateOptions && updateOptions.length >= 2) {
      setSelectedUpdate(updateOptions[0]);
      setCompareUpdate(updateOptions[1]);
    }
  }, [updateOptions]);

  const parseDateTime = (dateStr: string) => {
    if (dateStr === "all") return null;
    
    const [hourStr, datePartStr] = dateStr.split('h ');
    const [day, month, year] = datePartStr.trim().split('-').map(Number);
    const hour = parseInt(hourStr);
    
    if (isNaN(hour) || isNaN(day) || isNaN(month) || isNaN(year)) {
      console.error('Invalid date format:', dateStr);
      return null;
    }
    
    // Subtract 1 from hour to match database timestamps
    return { hour: hour - 1, day, month, year };
  };

  const { data: changes, isLoading } = useQuery({
    queryKey: ["tree-changes", selectedUpdate, compareUpdate],
    enabled: selectedUpdate !== "all" && compareUpdate !== "all" && selectedUpdate !== compareUpdate,
    queryFn: async () => {
      if (selectedUpdate === "all" || compareUpdate === "all" || selectedUpdate === compareUpdate) {
        return [];
      }

      const lastDate = parseDateTime(selectedUpdate);
      const prevDate = parseDateTime(compareUpdate);

      if (!lastDate || !prevDate) {
        console.error('Invalid date parameters:', { lastDate, prevDate });
        return [];
      }

      try {
        console.log('Querying with parameters:', {
          last_hour: lastDate.hour,
          last_day: lastDate.day,
          last_month: lastDate.month,
          last_year: lastDate.year,
          prev_hour: prevDate.hour,
          prev_day: prevDate.day,
          prev_month: prevDate.month,
          prev_year: prevDate.year
        });

        const { data, error } = await supabase.rpc('compare_categories', {
          last_hour: lastDate.hour,
          last_day: lastDate.day,
          last_month: lastDate.month,
          last_year: lastDate.year,
          prev_hour: prevDate.hour,
          prev_day: prevDate.day,
          prev_month: prevDate.month,
          prev_year: prevDate.year
        });

        if (error) {
          console.error('Error in compare_categories:', error);
          toast.error('Error comparing categories');
          throw error;
        }

        console.log('Query results:', data);
        return data || [];
      } catch (error) {
        console.error('Error in compare_categories:', error);
        toast.error('Error comparing categories');
        throw error;
      }
    },
  });

  const filteredChanges = changes?.filter(change => {
    if (selectedChangeType === "all") return true;
    return change[`${selectedChangeType}_change`] === "Changed";
  });

  // Sort the filtered changes based on product count difference
  const sortedChanges = filteredChanges?.sort((a, b) => {
    const getDifference = (change: typeof filteredChanges[0]) => {
      const last = parseInt(change.last_product_count) || 0;
      const prev = parseInt(change.prev_product_count) || 0;
      return last - prev;
    };

    const diffA = getDifference(a);
    const diffB = getDifference(b);

    return sortOrder === "asc" ? diffA - diffB : diffB - diffA;
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="container py-6 flex-1">
        <h1 className="text-4xl font-bold mb-8 text-center">Tree Changes</h1>
        
        <div className="mb-6 flex items-center gap-8">
          <UpdateSelector
            label="Update"
            value={selectedUpdate}
            onValueChange={setSelectedUpdate}
            updateOptions={updateOptions}
          />
          <UpdateSelector
            label="Compare to"
            value={compareUpdate}
            onValueChange={setCompareUpdate}
            updateOptions={updateOptions}
          />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Filter changes:</span>
            <Select value={selectedChangeType} onValueChange={setSelectedChangeType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select change type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Changes</SelectItem>
                <SelectItem value="name">Name Changes</SelectItem>
                <SelectItem value="title">Title Changes</SelectItem>
                <SelectItem value="description">Description Changes</SelectItem>
                <SelectItem value="level">Level Changes</SelectItem>
                <SelectItem value="url">URL Changes</SelectItem>
                <SelectItem value="product_count">Product Count Changes</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 ml-4">
              <span className="text-sm text-muted-foreground">
                ({filteredChanges?.length || 0} results)
              </span>
              <Select value={sortOrder} onValueChange={(value: "asc" | "desc") => setSortOrder(value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by product count" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Gained products</SelectItem>
                  <SelectItem value="asc">Lost products</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <ChangesTable changes={sortedChanges} isLoading={isLoading} />
      </main>
    </div>
  );
};

export default TreeChanges;
